import { apiFetch } from "./api";

/**
 * Server-custodied wallet operations. The backend holds the key (created with
 * the account on first email verify) and signs on the user's behalf; every
 * call here is authenticated and rate-limited per user (429 = "too many
 * signing requests").
 */

export interface WalletInfo {
	chain: string;
	address: string;
}

export interface SignResult {
	address: string;
	signature: `0x${string}`;
	hash: string;
}

export interface TypedDataPayload {
	domain: Record<string, unknown>;
	types: Record<string, readonly { name: string; type: string }[]>;
	primaryType: string;
	message: Record<string, unknown>;
}

export type TransferKind = "hl_deposit" | "external_withdraw";

export type TransferStatus =
	| "pending"
	| "topping_up"
	| "sent"
	| "confirmed"
	| "failed";

export interface Transfer {
	id: string;
	kind: TransferKind;
	to_address: string | null;
	amount_usd: string;
	status: TransferStatus;
	tx_hash: string | null;
	error: string | null;
	created_at: string;
	broadcast_at: string | null;
	confirmed_at: string | null;
}

export function fetchWallet(): Promise<WalletInfo> {
	return apiFetch<WalletInfo>("/v1/wallet", { method: "GET" });
}

/** EIP-191 personal_sign of a UTF-8 message. */
export function signMessage(message: string): Promise<SignResult> {
	return apiFetch<SignResult>("/v1/wallet/sign/message", {
		method: "POST",
		body: { chain: "evm", message, encoding: "utf8" },
	});
}

/** EIP-712 typed-data signature (Hyperliquid order/action signing). */
export function signTypedData(
	typedData: TypedDataPayload,
): Promise<SignResult> {
	return apiFetch<SignResult>("/v1/wallet/sign/typed-data", {
		method: "POST",
		body: { chain: "evm", typedData },
	});
}

/**
 * Submit an async on-chain transfer executed by the backend. `hl_deposit`
 * ignores `to_address` (the HL bridge is forced server-side);
 * `external_withdraw` requires it. One active transfer per user — a second
 * submit while one is in flight fails with "a transfer is already in
 * progress". Poll `getTransfer(id)` for status.
 */
export function createTransfer(input: {
	kind: TransferKind;
	amount_usd: string;
	to_address?: string;
	idempotency_key: string;
}): Promise<Transfer> {
	return apiFetch<Transfer>("/v1/wallet/transfers", {
		method: "POST",
		body: input,
	});
}

export function getTransfer(id: string): Promise<Transfer> {
	return apiFetch<Transfer>(`/v1/wallet/transfers/${id}`, { method: "GET" });
}

export function listTransfers(): Promise<Transfer[]> {
	return apiFetch<Transfer[]>("/v1/wallet/transfers", { method: "GET" });
}
